import { Icon as IconifyIcon } from "@iconify/react";
import type { ComponentProps, SVGProps } from "react";

import { cn } from "~/lib/utils";
import { AccommodationIcon } from "./accommodation";
import { ActivityIcon } from "./activity";
import { DeskIcon } from "./desk";
import { PetFriendlyIcon } from "./pet-friendly";
import { BackcountryIcon } from "./backcountry";
import { CompassNeedleIcon } from "./compass-needle";
import { BarIcon } from "./bar";
import { BikeParkIcon } from "./bike-park";
import { BikeShopIcon } from "./bike-shop";
import { CafeIcon } from "./cafe";
import { CampgroundIcon } from "./campground";
import { CheckfrontIcon } from "./checkfront";
import { ConvenienceStoreIcon } from "./convenience-store";
import { CoworkingSpaceIcon } from "./coworking-space";
import { DepartmentStoreIcon } from "./department-store";
import { EndorphineIcon } from "./endorphine";
import { FreshMarketIcon } from "./fresh-market";
import { GroceryIcon } from "./grocery-store";
import { GymIcon } from "./gym";
import { HikingIcon } from "./hiking";
import { LibraryIcon } from "./library";
import { MarkerIcon } from "./marker";
import { MtbTrailIcon } from "./mtb-trail";
import { MoreVerticalIcon } from "./more-vertical";
import { NaverMapIcon } from "./naver-map";
import { ParkIcon } from "./public-park";
import { PharmacyIcon } from "./pharmacy";
import { PublicTransportIcon } from "./public-transport";
import { RestaurantIcon } from "./restaurant";
import { SkiBoardShopIcon } from "./ski-board-shop";
import { SkiResortIcon } from "./ski-resort";
import { SpaIcon } from "./spa";
import { SwimmingPoolIcon } from "./swimming-pool";
import { TrailforksIcon } from "./trailforks";
import {
  AirConditionerIcon,
  BikeStorageIcon,
  ElevatorIcon,
  FireplaceIcon,
  FreeParkingPremisesIcon,
  FullyFurnishedKitchenIcon,
  KitchenIcon,
  OutdoorPoolIcon,
  PaidParkingPremisesIcon,
  SmartLockIcon,
  TelevisionIcon,
  TowelsSoapIcon,
  WasherDryerIcon,
  WasherDryingRackIcon,
} from "./amenities";

type SvgIconComponent = (props: SVGProps<SVGSVGElement>) => JSX.Element;

const iconifyIcons = {
  "add-place": "solar:map-point-add-bold",
  "arrow-right": "solar:arrow-right-linear",
  transfer: "solar:transfer-horizontal-linear",
  calendar: "solar:calendar-minimalistic-bold",
  compass: "solar:compass-big-bold",
  back: "solar:arrow-left-linear",
  "zoom-in": "typcn:plus",
  "zoom-out": "typcn:minus",
  home: "solar:home-2-linear",
  menu: "solar:hamburger-menu-linear",
  check: "solar:check-circle-bold",
  "check-circle": "solar:check-circle-bold",
  info: "solar:info-circle-bold",
  warning: "solar:danger-triangle-bold",
  error: "solar:close-circle-bold",
  "chevron-right": "solar:alt-arrow-right-linear",
  close: "solar:close-circle-bold",
  copy: "solar:copy-bold",
  "delete-image": "solar:close-circle-bold",
  edit: "solar:pen-2-linear",
  "edit-place": "solar:pen-2-linear",
  email: "solar:letter-linear",
  "external-link": "solar:arrow-right-up-linear",
  "map-outline": "solar:map-outline",
  "map-pin": "solar:map-point-bold",
  layout: "solar:window-frame-bold",
  link: "solar:link-bold",
  list: "solar:list-bold",
  "log-out": "solar:logout-2-linear",
  loader: "solar:refresh-bold",
  map: "solar:map-bold",
  phone: "solar:phone-linear",
  search: "solar:magnifer-linear",
  "search-clear": "solar:close-circle-linear",
  share: "solar:share-bold",
  text: "solar:text-bold",
  trash: "solar:trash-bin-trash-linear",
  "user-profile": "solar:user-circle-bold",
  users: "solar:users-group-rounded-bold",
  // Accommodation stats
  "wifi-speed": "material-symbols:speed-rounded",
  "wifi-stability": "streamline-flex:wifi-antenna-solid",
  "wifi-slow": "grommet-icons:wifi-none",
  "wifi-regular": "grommet-icons:wifi-low",
  "wifi-fast": "grommet-icons:wifi-medium",
  "wifi-very-fast": "grommet-icons:wifi",
  bed: "solar:bed-bold",
  bedroom: "material-symbols:door-open-rounded",
  bathroom: "solar:bath-bold",
  // Pricing
  "price-tag": "solar:tag-price-outline",
  // Guest policies
  "child-friendly": "ic:round-child-friendly",
  // Backcountry related place icons
  cabin: "lucide-lab:cabin",
  "hot-spring": "streamline:hot-spring-solid",
  hut: "lucide-lab:cabin",
  "gondola-lift": "material-symbols:gondola-lift-rounded",
  parking: "ic:round-local-parking",
  "shuttle-truck": "glyphs:pickup-truck-bold",
  // Brand icons
  airbnb: "logos:airbnb-icon",
  alltrails: "simple-icons:alltrails",
  "booking-com": "simple-icons:bookingdotcom",
  expedia: "simple-icons:expedia",
  facebook: "logos:facebook",
  foursquare: "simple-icons:foursquare",
  "google-maps": "logos:google-maps",
  instagram: "skill-icons:instagram",
  "kakao-map": "simple-icons:kakao",
  komoot: "simple-icons:komoot",
  linkedin: "logos:linkedin-icon",
  pinterest: "logos:pinterest",
  strava: "simple-icons:strava",
  threads: "simple-icons:threads",
  tiktok: "logos:tiktok-icon",
  tripadvisor: "simple-icons:tripadvisor",
  waze: "simple-icons:waze",
  whatsapp: "logos:whatsapp-icon",
  x: "ri:twitter-x-fill",
  yelp: "simple-icons:yelp",
  youtube: "logos:youtube-icon",
  zomato: "simple-icons:zomato",
} as const satisfies Record<string, string>;

const customIcons = {
  accommodation: AccommodationIcon,
  activity: ActivityIcon,
  "air-conditioner": AirConditionerIcon,
  backcountry: BackcountryIcon,
  bar: BarIcon,
  "bike-storage": BikeStorageIcon,
  "compass-needle": CompassNeedleIcon,
  "bike-park": BikeParkIcon,
  "bike-shop": BikeShopIcon,
  cafe: CafeIcon,
  campground: CampgroundIcon,
  checkfront: CheckfrontIcon,
  "convenience-store": ConvenienceStoreIcon,
  "coworking-space": CoworkingSpaceIcon,
  "department-store": DepartmentStoreIcon,
  desk: DeskIcon,
  elevator: ElevatorIcon,
  endorphine: EndorphineIcon,
  fireplace: FireplaceIcon,
  "free-parking-premises": FreeParkingPremisesIcon,
  "fully-furnished-kitchen": FullyFurnishedKitchenIcon,
  "fresh-market": FreshMarketIcon,
  "grocery-store": GroceryIcon,
  gym: GymIcon,
  hiking: HikingIcon,
  kitchen: KitchenIcon,
  library: LibraryIcon,
  marker: MarkerIcon,
  massage: MarkerIcon,
  "mtb-trail": MtbTrailIcon,
  "more-vertical": MoreVerticalIcon,
  "naver-map": NaverMapIcon,
  "outdoor-pool": OutdoorPoolIcon,
  "paid-parking-premises": PaidParkingPremisesIcon,
  "pet-friendly": PetFriendlyIcon,
  pharmacy: PharmacyIcon,
  "public-park": ParkIcon,
  "public-transport": PublicTransportIcon,
  restaurant: RestaurantIcon,
  "ski-board-shop": SkiBoardShopIcon,
  "ski-resort": SkiResortIcon,
  "smart-lock": SmartLockIcon,
  spa: SpaIcon,
  "swimming-pool": SwimmingPoolIcon,
  television: TelevisionIcon,
  "towels-soap": TowelsSoapIcon,
  trailforks: TrailforksIcon,
  "washer-dryer": WasherDryerIcon,
  "washer-drying-rack": WasherDryingRackIcon,
  "yoga-center": MarkerIcon,
} as const satisfies Record<string, SvgIconComponent>;

export type IconType = keyof typeof iconifyIcons | keyof typeof customIcons;

type IconProps = Omit<ComponentProps<typeof IconifyIcon>, "icon"> & {
  i: IconType;
};

export function Icon({ i, className, ...props }: IconProps) {
  const classes = cn("shrink-0", className);

  if (i in customIcons) {
    const CustomIcon = customIcons[i as keyof typeof customIcons];
    return <CustomIcon className={classes} {...props} />;
  }

  return <IconifyIcon icon={iconifyIcons[i as keyof typeof iconifyIcons]} className={classes} {...props} />;
}
